import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import ArrowRightIcon from 'mdi-react/ArrowRightIcon';
import { bool, func, number, object, string } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { I18n } from 'utils/i18n';
import withNavigate from 'utils/withNavigate';
import connector from './connector';
import formik from './formik';

const linkSx = {
  cursor: 'pointer',
  color: 'primary.dark',
  display: 'inline',
};

const buttonLinkSx = {
  p: 0,
  minHeight: 'auto',
  display: 'inline',
  borderRadius: 0,
  color: 'primary.dark',
};

const ConfirmationForm = ({
  actions,
  errors,
  handleBlur,
  handleChange,
  handleSubmit,
  isAcceptTerms,
  isSubmitting,
  keepSessionOpen,
  login,
  loginType = 'sms',
  submitCount,
  values,
}) => {
  const navigate = useNavigate();
  const locale = useSelector(state => state.i18n.locale);
  useEffect(() => {
    if (!login.phone) {
      navigate('/auth/login');
    }
  }, [login.phone, navigate]);
  const timeout = useRef();

  const [inTimeout, setInTimeout] = useState(false);

  useEffect(
    () => () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    },
    [],
  );

  const sendSMS = options => () => {
    setInTimeout(true);
    timeout.current = setTimeout(() => {
      setInTimeout(false);
    }, 10000);
    actions.login.sendSMS(login.phone, options);
  };

  const hasSubmitted = submitCount > 0;
  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <TextField
        style={{ height: 0, display: 'block', padding: 0, margin: 0, overflow: 'hidden' }}
        type="text"
        value={login.form && login.form.phone}
        autoComplete="username phone"
      />
      <FormControl fullWidth>
        {loginType === 'sms' ? (
          <PatternFormat
            error={!!(errors.code && hasSubmitted)}
            helperText={hasSubmitted && errors.code && I18n.t(`LoginConfirmation.${errors.code}`)}
            name="code"
            placeholder={I18n.t('LoginConfirmation.sms code placeholder')}
            customInput={TextField}
            format="## ## ##"
            mask="_"
            type="text"
            value={values.code}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="one-time-code"
          />
        ) : (
          <TextField
            error={!!(errors.code && hasSubmitted)}
            helperText={hasSubmitted && errors.code}
            placeholder={I18n.t('LoginConfirmation.password placeholder')}
            name="code"
            type="password"
            value={values.code}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="current-password"
          />
        )}
      </FormControl>
      {!isAcceptTerms && (
        <FormControl required error={errors.terms} component="fieldset" sx={{ display: 'block' }}>
          <FormControlLabel
            control={
              <Checkbox
                name="terms"
                helperText={hasSubmitted && errors.code}
                checked={values.terms?.[0] === 'on'}
                onChange={handleChange}
                value="on"
                sx={hasSubmitted && errors.terms ? { color: 'error.main' } : undefined}
                color={hasSubmitted && errors.terms ? 'error' : 'primary'}
              />
            }
            label={
              <Typography
                component="span"
                sx={{ display: 'inline' }}
                color={hasSubmitted && errors.terms ? 'error' : 'primary'}
              >
                {I18n.t('LoginConfirmation.i accept the')}{' '}
                <Link component={RouterLink} to="/auth/login/terms" sx={linkSx}>
                  {I18n.t('LoginConfirmation.terms and conditions')}
                </Link>{' '}
                {I18n.t('LoginConfirmation.and the')}{' '}
                <Link component={RouterLink} to="/auth/login/privacy" sx={linkSx}>
                  {I18n.t('LoginConfirmation.privacy policy')}
                </Link>
              </Typography>
            }
          />
          {hasSubmitted && errors.terms && (
            <FormHelperText sx={{ mt: 0, mb: '8px' }}>
              {I18n.t('LoginConfirmation.You must accept')}
            </FormHelperText>
          )}
        </FormControl>
      )}
      <Button
        fullWidth
        variant="contained"
        type="submit"
        sx={{ mt: '20px' }}
        color="primary"
        disabled={isSubmitting}
      >
        {I18n.t('LoginConfirmation.next')}
        <SvgIcon sx={{ fontSize: 17 }}>
          <ArrowRightIcon />
        </SvgIcon>
      </Button>
      {loginType === 'sms' ? (
        <Typography sx={{ fontSize: 14, mt: '28px', mr: '20px' }} variant="caption">
          {I18n.t('LoginConfirmation.we sent sms to', { phone: login.phone })}{' '}
          <Link component={RouterLink} to="/auth/login" sx={linkSx}>
            {I18n.t('LoginConfirmation.wrong number link')}
          </Link>{' '}
          {I18n.t('LoginConfirmation.or')}{' '}
          <Button
            component="span"
            variant="text"
            role="button"
            disabled={inTimeout}
            tabIndex={-1}
            sx={buttonLinkSx}
            onClick={sendSMS({ keepSessionOpen, locale })}
          >
            {I18n.t('LoginConfirmation.resend link')}
          </Button>
          {inTimeout && (
            <Typography
              sx={{ fontSize: 14, mt: '28px', mr: '20px' }}
              align="center"
              variant="caption"
            >
              {I18n.t('LoginConfirmation.wait 10 seconds')}
            </Typography>
          )}
        </Typography>
      ) : null}
    </form>
  );
};

ConfirmationForm.propTypes = {
  actions: object.isRequired,
  errors: object.isRequired,
  handleBlur: func.isRequired,
  handleChange: func.isRequired,
  handleSubmit: func.isRequired,
  isAcceptTerms: bool.isRequired,
  isSubmitting: bool.isRequired,
  keepSessionOpen: bool.isRequired,
  login: object.isRequired,
  loginType: string,
  submitCount: number.isRequired,
  values: object.isRequired,
};

export default connector(withNavigate(formik(ConfirmationForm)));
